import type { GetEditorReviewCompositionEntityEnum } from "@eu.mdotm.api/sphere";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import { instrumentsRepository } from "./repository";

/**
 * Navigates to the review screen, but only once there is something there to review.
 *
 * Entity details pages redirect on a `REVIEW` status, and that status is not only reached by an entity
 * that needs attention: the backend publishes it while the entity is still ingesting and then settles on
 * `READY` a few hundred milliseconds later with an empty review composition. The redirect is one-way —
 * `ReviewInstruments` reads the summary with `useQueryNoRefetch`, so it never observes the status
 * changing and offers no way back except Cancel — so acting on the status alone strands the user on a
 * screen showing "No Rows To Show" under a banner telling them their instruments need attention.
 *
 * Checking the composition first costs one request, and only on a status that is rare to begin with.
 * When it comes back empty the caller stays where it is, and its own polling picks up the real status.
 *
 * A failed check navigates anyway: this guard exists to suppress a redirect nobody wanted, and it must
 * never become the reason an entity that genuinely needs reviewing is left unreachable.
 *
 * Every caller so far is an `onSuccess` handler, which is synchronous, so
 * {@link navigateToReviewIfAnythingToReviewSync} is what they actually reach for. This one stays exported
 * and honestly `async` for anyone who needs to await the decision.
 */
export async function navigateToReviewIfAnythingToReview(
	uuid: string,
	entity: GetEditorReviewCompositionEntityEnum,
	navigate: () => void,
): Promise<void> {
	if (entity === "BENCHMARK") {
		// The backend resolves an editor by (entity, operation) and has none registered for `BENCHMARK` —
		// no service implements `BenchmarkTargetEntity`, for review or for anything else — so asking would
		// only ever answer "Editor not found.". Navigate as before rather than firing a doomed request.
		navigate();
		return;
	}

	try {
		const review = await instrumentsRepository.reviewComposition.fetchQuery(uuid, entity);
		if ((review.composition?.length ?? 0) > 0) {
			navigate();
		}
	} catch {
		navigate();
	}
}

/**
 * {@link navigateToReviewIfAnythingToReview}, pre-wrapped for the synchronous `onSuccess` callbacks that
 * are its only callers today — so the `unpromisify` boilerplate lives here once instead of at every call
 * site. Rejections reach `console.warn` rather than becoming an unhandled promise; there is nothing to
 * await, because the whole point is that navigation may not happen at all.
 */
export const navigateToReviewIfAnythingToReviewSync = unpromisify(navigateToReviewIfAnythingToReview);
